/**
 * Getters for the [target_access_permissions store]{link module:store/target_access_permissions}
 *
 * @module store/target_access_permissions/getters
 */

export const deviceIdsToSensorIdsWithTargetAccessPermissions = (state, getters, rootState, rootGetters) => {
  const allDeviceIdsToSensorIds = rootGetters['measurements/deviceIdsToSensorIds']

  const idsWithPermission = {
    devices: [],
    sensors: [],
    status_sensors: []
  }
  for (const permission of Object.values(state.permissions)) {
    idsWithPermission[permission.targetType].push(permission.targetId)
  }

  const deviceIdsToSensorIdsWithPermissions = {}
  for (const [deviceId, sensorMap] of Object.entries(allDeviceIdsToSensorIds)) {
    const dId = parseInt(deviceId)
    if (!idsWithPermission.devices.includes(dId)) continue

    deviceIdsToSensorIdsWithPermissions[dId] = {
      sensors: sensorMap.sensors.filter((sensorId) => idsWithPermission.sensors.includes(sensorId)),
      status_sensors: sensorMap.status_sensors.filter((statusSensorId) =>
        idsWithPermission.status_sensors.includes(statusSensorId)
      )
    }

    if (
      !deviceIdsToSensorIdsWithPermissions[dId].sensors.length &&
      !deviceIdsToSensorIdsWithPermissions[dId].status_sensors.length
    )
      delete deviceIdsToSensorIdsWithPermissions[dId]
  }

  return deviceIdsToSensorIdsWithPermissions
}
export const getTargetAccessPermissionsForUser = (state) => {
  return ({ userId }) =>
    Object.values(state.permissions).filter(
      (targetAccessPermission) => targetAccessPermission.userId === parseInt(userId)
    )
}
export const getTargetAccessPermissionOptionsForUser = (state, _, rootState) => {
  return ({ locale, fullyShownDeviceIds = [], userId }) => {
    const options = []
    const targetAccessPermissions = Object.values(state.permissions).filter(
      (permission) => permission.userId === parseInt(userId)
    )
    const relationsForTargetAccessPermissions = getRelationsForTargetAccessPermissions({
      rootState,
      targetAccessPermissions
    })
    const sortedTypedRelationIds = relationsForTargetAccessPermissions.reduce((prev, rel) => {
      if (!rel) return prev

      let ids = [`devices_${rel.id}`]
      if (fullyShownDeviceIds.includes(rel.id)) {
        ids = ids.concat(
          rel.sensorRelations.map((sR) => {
            if (sR.hasSensorWithType({ type: 'sensors' })) return `sensors_${sR.id}`
            if (sR.hasSensorWithType({ type: 'status_sensors' })) return `status_sensors_${sR.id}`
          })
        )
      }
      return [...prev, ...ids]
    }, [])

    const typedRelationIdToLabel = relationsForTargetAccessPermissions.reduce((prev, rel) => {
      if (!rel) return prev

      let newEntries = {
        [`devices_${rel.id}`]: rel.label
      }
      if (fullyShownDeviceIds.includes(rel.id)) {
        newEntries = {
          ...newEntries,
          ...rel.sensorRelations.reduce((pr, sR) => {
            let id = ''
            if (sR.hasSensorWithType({ type: 'sensors' })) id = `sensors_${sR.id}`
            if (sR.hasSensorWithType({ type: 'status_sensors' })) id = `status_sensors_${sR.id}`

            return {
              [id]: sR.label,
              ...pr
            }
          }, {})
        }
      }
      return {
        ...prev,
        ...newEntries
      }
    }, [])

    for (const targetAccessPermission of targetAccessPermissions) {
      const typedTargetId = `${targetAccessPermission.targetType}_${targetAccessPermission.targetId}`

      if (!sortedTypedRelationIds.includes(typedTargetId)) continue

      options.push({
        id: targetAccessPermission.id,
        name: typedRelationIdToLabel[typedTargetId],
        targetId: targetAccessPermission.targetId,
        targetType: targetAccessPermission.targetType,
        timeRanges: targetAccessPermission.listTimeRanges({ locale }),
        typedTargetId,
        onlyOpenTimeRanges: targetAccessPermission.onlyOpenTimeRanges()
      })
    }

    return options.sort((a, b) => {
      const indexA = sortedTypedRelationIds.indexOf(a.typedTargetId)
      const indexB = sortedTypedRelationIds.indexOf(b.typedTargetId)
      if (indexA < indexB) return -1
      if (indexB < indexA) return 1
      return 0
    })
  }
}
export const newDeviceTargetOptions = (state, getters, rootState, rootGetters) => {
  const existingDeviceTargetIds = Object.values(state.permissions)
    .filter((targetAccessPermission) => targetAccessPermission.targetType === 'devices')
    .map((targetAccessPermission) => parseInt(targetAccessPermission.targetId))

  return rootGetters['measurements/getDeviceOptions']({
    onlyPhysicalDevices: true,
    skippedDeviceIds: existingDeviceTargetIds
  })
}
export const newSensorTargetOptions = (state, getters, rootState, rootGetters) => {
  const existingSensorTargetIds = Object.values(state.permissions)
    .filter((targetAccessPermission) => ['sensors', 'status_sensors'].includes(targetAccessPermission.targetType))
    .map((targetAccessPermission) => targetAccessPermission.targetId)

  return rootGetters['measurements/getSensorOptions']({ skippedSensorIds: existingSensorTargetIds })
}
export const typedTargetIdToPermissionId = (state) =>
  Object.values(state.permissions).reduce(
    (prev, perm) => ({ ...prev, [`${perm.targetType}_${perm.targetId}`]: perm.id }),
    {}
  )
export const usersToRestrictionState = (state) => {
  const usersRestrictionState = {}
  const usersToTargetAccessPermissions = parseUserIdsToTargetAccessPermissions(Object.values(state.permissions))

  for (const fullPermissionUserId of state.fullPermissionUserIds) {
    usersRestrictionState[fullPermissionUserId] = 'open'
  }
  for (const restrictedPermissionUserId of state.restrictedPermissionUserIds) {
    usersRestrictionState[restrictedPermissionUserId] = usersToTargetAccessPermissions[restrictedPermissionUserId]
      ? 'restricted'
      : 'closed'
  }
  return usersRestrictionState
}
export const usersToTargetLabels = (state, _, rootState) => {
  const usersToTargetAccessPermissions = parseUserIdsToTargetAccessPermissions(Object.values(state.permissions))
  const usersToTargetLabels = {}

  for (const [userId, targetAccessPermissions] of Object.entries(usersToTargetAccessPermissions)) {
    const relationsForTargetAccessPermissions = getRelationsForTargetAccessPermissions({
      rootState,
      targetAccessPermissions
    })
    const targetIds = targetAccessPermissions.map((x) => x.targetId)

    usersToTargetLabels[userId] = relationsForTargetAccessPermissions.reduce((prev, relation) => {
      return [...prev, relation.label, ...relation.sensorLabels({ whitelistedSensorIds: targetIds })]
    }, [])
  }

  return usersToTargetLabels
}
export const usersToTargetListItems = (state, _, rootState) => {
  return ({ usersWithFullyShownDevices = {} } = {}) => {
    const usersToTargetAccessPermissions = parseUserIdsToTargetAccessPermissions(Object.values(state.permissions))
    const usersToTargetListItems = {}

    for (const [userId, targetAccessPermissions] of Object.entries(usersToTargetAccessPermissions)) {
      const relationsForTargetAccessPermissions = getRelationsForTargetAccessPermissions({
        rootState,
        targetAccessPermissions
      })
      const targetIds = targetAccessPermissions.map((x) => x.targetId)
      usersToTargetListItems[userId] = relationsForTargetAccessPermissions.reduce((prev, relation) => {
        prev.push({ id: relation.id, label: relation.label })

        if (!usersWithFullyShownDevices[userId]?.includes(relation.id)) return prev

        const sensorLabels = relation.sensorLabels({ whitelistedSensorIds: targetIds })
        for (const label of sensorLabels) {
          prev.push({ label, classes: 'fst-italic' })
        }
        return prev
      }, [])
    }

    return usersToTargetListItems
  }
}

const getRelationsForTargetAccessPermissions = ({ rootState, targetAccessPermissions }) => {
  const relations = {}
  const deviceRelations = Object.values(rootState.measurements?.deviceRelations)

  const targetIdToDeviceRelation = {
    devices: {},
    sensors: {},
    statusSensors: {}
  }
  for (const deviceRelation of deviceRelations) {
    for (const deviceId of deviceRelation.deviceIds()) {
      targetIdToDeviceRelation.devices[deviceId] = deviceRelation
    }
    for (const sensorId of deviceRelation.sensorIds({ type: 'sensors' })) {
      targetIdToDeviceRelation.sensors[sensorId] = deviceRelation
    }
    for (const sensorId of deviceRelation.sensorIds({ type: 'status_sensors' })) {
      targetIdToDeviceRelation.statusSensors[sensorId] = deviceRelation
    }
  }
  for (const targetAccessPermission of targetAccessPermissions) {
    const targetId = targetAccessPermission.targetId
    const targetType = targetAccessPermission.targetType
    let relation
    if (targetType === 'devices') {
      relation = targetIdToDeviceRelation.devices[targetId]
    } else if (targetType === 'sensors') {
      relation = targetIdToDeviceRelation.sensors[targetId]
    } else if (targetType === 'status_sensors') {
      relation = targetIdToDeviceRelation.statusSensors[targetId]
    }
    if (!relation) continue
    if (!relations[relation?.id]) relations[relation?.id] = relation
  }

  return Object.values(relations).sort((a, b) => a.label.localeCompare(b.label))
}

const parseUserIdsToTargetAccessPermissions = (targetAccessPermissions) => {
  const usersToTargetAccessPermissions = {}

  for (const targetAccessPermission of targetAccessPermissions) {
    const userId = targetAccessPermission.userId

    if (!usersToTargetAccessPermissions[userId]) usersToTargetAccessPermissions[userId] = []

    usersToTargetAccessPermissions[userId].push(targetAccessPermission)
  }

  return usersToTargetAccessPermissions
}
